import { useState, useCallback, useEffect } from 'react';

const GOOGLE_CLIENT_ID = import.meta.env.VITE_GOOGLE_CLIENT_ID;
const GOOGLE_API_KEY = import.meta.env.VITE_GOOGLE_API_KEY;
const SCOPES = 'https://www.googleapis.com/auth/drive.readonly';
const DISCOVERY_DOCS = ['https://www.googleapis.com/discovery/v1/apis/drive/v3/rest'];

export interface GoogleDriveFile {
  id: string;
  name: string;
  mimeType: string;
  sizeBytes: number;
  downloadUrl: string;
}

export interface GoogleDrivePickerResult {
  files: GoogleDriveFile[];
  accessToken: string | null;
}

interface UseGoogleDrivePickerReturn {
  openPicker: () => Promise<GoogleDrivePickerResult>;
  isLoading: boolean;
  error: string | null;
}

declare global {
  interface Window {
    gapi: any;
    google: any;
  }
}

const STORAGE_KEY = 'google_drive_access_token';
const TOKEN_EXPIRY_KEY = 'google_drive_token_expiry';

// Load external script
const loadScript = (src: string, id: string): Promise<void> => {
  return new Promise((resolve, reject) => {
    if (document.getElementById(id)) {
      resolve();
      return;
    }
    const script = document.createElement('script');
    script.id = id;
    script.src = src;
    script.async = true;
    script.defer = true;
    script.onload = () => resolve();
    script.onerror = () => reject(new Error(`Failed to load script: ${src}`));
    document.head.appendChild(script);
  });
};

export const useGoogleDrivePicker = (): UseGoogleDrivePickerReturn => {
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [gapiLoaded, setGapiLoaded] = useState(false);
  const [gisLoaded, setGisLoaded] = useState(false);

  // Load Google APIs on mount
  useEffect(() => {
    const loadAPIs = async () => {
      try {
        // Load GAPI
        await loadScript('https://apis.google.com/js/api.js', 'google-api');
        await new Promise<void>((resolve) => {
          window.gapi.load('picker', () => resolve());
        });
        setGapiLoaded(true);

        // Load Google Identity Services
        await loadScript('https://accounts.google.com/gsi/client', 'google-gsi');
        setGisLoaded(true);
      } catch (err) {
        console.error('Failed to load Google APIs:', err);
        setError('Failed to load Google APIs');
      }
    };

    loadAPIs();
  }, []);

  const getStoredToken = useCallback((): string | null => {
    const token = localStorage.getItem(STORAGE_KEY);
    const expiry = localStorage.getItem(TOKEN_EXPIRY_KEY);
    
    if (token && expiry) {
      const expiryTime = parseInt(expiry, 10);
      if (Date.now() < expiryTime) {
        return token;
      }
      // Token expired, clear it
      localStorage.removeItem(STORAGE_KEY);
      localStorage.removeItem(TOKEN_EXPIRY_KEY);
    }
    return null;
  }, []);

  const storeToken = useCallback((token: string, expiresIn: number) => {
    localStorage.setItem(STORAGE_KEY, token);
    // Store expiry time (current time + expires_in seconds - 60 second buffer)
    const expiryTime = Date.now() + (expiresIn - 60) * 1000;
    localStorage.setItem(TOKEN_EXPIRY_KEY, expiryTime.toString());
  }, []);

  const requestAccessToken = useCallback((): Promise<string> => {
    return new Promise((resolve, reject) => {
      const origin = window.location.origin;
      console.log('Google OAuth - Using origin:', origin);
      console.log('Google OAuth - Client ID:', GOOGLE_CLIENT_ID);
      
      const tokenClient = window.google.accounts.oauth2.initTokenClient({
        client_id: GOOGLE_CLIENT_ID,
        scope: SCOPES,
        callback: (response: any) => {
          if (response.error) {
            console.error('Google OAuth error:', response.error, response.error_description);
            
            // Handle specific errors with helpful messages
            if (response.error === 'redirect_uri_mismatch' || 
                response.error_description?.includes('redirect_uri_mismatch')) {
              reject(new Error('Google Drive authentication is being configured. Please try again in a few minutes.'));
              return;
            }
            
            if (response.error === 'popup_closed_by_user') {
              reject(new Error('Authentication was cancelled'));
              return;
            }
            
            if (response.error === 'access_denied') {
              reject(new Error('Access was denied. Please grant permission to access Google Drive.'));
              return;
            }
            
            reject(new Error(response.error_description || response.error));
            return;
          }
          storeToken(response.access_token, response.expires_in);
          resolve(response.access_token);
        },
        error_callback: (error: any) => {
          console.error('Google OAuth error callback:', error);
          
          if (error.type === 'popup_closed') {
            reject(new Error('Authentication popup was closed'));
            return;
          }
          
          if (error.type === 'popup_failed_to_open') {
            reject(new Error('Failed to open authentication popup. Please allow popups for this site.'));
            return;
          }
          
          reject(new Error('Authentication failed. Please try again.'));
        },
      });
      tokenClient.requestAccessToken({ prompt: '' });
    });
  }, [storeToken]);

  const createPicker = useCallback((accessToken: string): Promise<GoogleDriveFile[]> => {
    return new Promise((resolve) => {
      const origin = window.location.origin;
      console.log('Google Picker - Using origin:', origin);
      
      const view = new window.google.picker.DocsView(window.google.picker.ViewId.DOCS);
      view.setMimeTypes('image/jpeg,image/png,image/gif,image/webp,video/mp4,video/quicktime,video/x-msvideo,video/avi');
      view.setIncludeFolders(true);
      view.setSelectFolderEnabled(false);

      const picker = new window.google.picker.PickerBuilder()
        .addView(view)
        .setOAuthToken(accessToken)
        .setDeveloperKey(GOOGLE_API_KEY)
        .setOrigin(origin)
        .setCallback(async (data: any) => {
          if (data.action === window.google.picker.Action.PICKED) {
            const files: GoogleDriveFile[] = data.docs.map((doc: any) => ({
              id: doc.id,
              name: doc.name,
              mimeType: doc.mimeType,
              sizeBytes: doc.sizeBytes || 0,
              downloadUrl: `https://www.googleapis.com/drive/v3/files/${doc.id}?alt=media&key=${GOOGLE_API_KEY}`,
            }));
            resolve(files);
          } else if (data.action === window.google.picker.Action.CANCEL) {
            resolve([]);
          }
        })
        .enableFeature(window.google.picker.Feature.MULTISELECT_ENABLED)
        .setTitle('Select Images or Videos')
        .build();

      picker.setVisible(true);
    });
  }, []);

  const openPicker = useCallback(async (): Promise<GoogleDrivePickerResult> => {
    setError(null);
    setIsLoading(true);

    try {
      if (!gapiLoaded || !gisLoaded) {
        throw new Error('Google APIs not loaded yet. Please try again.');
      }

      // Check for stored token first
      let accessToken = getStoredToken();

      // If no valid token, request one
      if (!accessToken) {
        accessToken = await requestAccessToken();
      }

      // Open picker with token
      const files = await createPicker(accessToken);
      return { files, accessToken };
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Failed to open Google Drive picker';
      setError(errorMessage);
      console.error('Google Drive Picker error:', err);
      return { files: [], accessToken: null };
    } finally {
      setIsLoading(false);
    }
  }, [gapiLoaded, gisLoaded, getStoredToken, requestAccessToken, createPicker]);

  return {
    openPicker,
    isLoading,
    error,
  };
};
